import React, { useEffect, useState } from 'react'
import { Modal, View } from 'oio-react'
import PropTypes from 'prop-types'
import FileInput from 'react-files'
import { Link, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom'
import useFileUploads from 'src/components/utils/useFileUploads'
import { useUpdateNote, useRequestNoteLock } from 'src/graphql/hooks'
import ImagePreview from './ImagePreview'

const NoteMediaBlock = ({ note }) => {
   const history = useHistory()
   const match = useRouteMatch()

   const { updateNote } = useUpdateNote()
   const { requestNoteLock } = useRequestNoteLock()
   const { uploadFile, uploadsInProgress, resetFileUploadState } =
      useFileUploads({ targetId: note?.initiative.id, targetType: 'initiative' })

   // Note Files
   const initialNoteFiles = note?.content.elements?.[0]?.fileInstances
      .map(instance => instance.file) ?? []

   const [noteFiles, setNoteFiles] = useState(initialNoteFiles)

   // Save files to Note content block
   const updateNoteFileList = async (newNoteFiles) => {
      setNoteFiles(newNoteFiles)

      const fileIds = newNoteFiles.map(f => f.id)
      const result = await requestNoteLock({ id: note.id })

      if (result.data.requestNoteLock.lockedByCurrentConnection) {
         updateNote({ id: note.id }, {
            content: [{
               operation: 'updateFileList',
               updateFileList: {
                  fileIds,
                  id: note.content.elements[0].id
               }
            }]
         }, { fetchPolicy: 'no-cache' })
      }
   }

   // Upload File
   const handleFileUpload = async (filesToUpload) => {
      if (!filesToUpload.length) {
         return
      }

      try {
         const result = await uploadFile({
            file: filesToUpload[0],
            purpose: 'contentEmbed'
         })

         const newFile = {
            id: result.fileId,
            name: filesToUpload[0].name,
            thumbnailUrlW500: result.uploadedMediaUrl
         }

         updateNoteFileList([...noteFiles, newFile])
         resetFileUploadState()
      } catch (err) {
         // TODO: Improve this
         // See PR #8
         window.alert(err.message)
      }
   }

   const handleDeleteFile = (fileId) => {
      updateNoteFileList(noteFiles.filter(file => file.id !== fileId))
   }

   // When files are either added or removed, trigger save
   useEffect(() => {
      setNoteFiles(initialNoteFiles)
   }, [note?.id])

   return (
      <>
         <FileInput
            accepts={['image/*']}
            clickable={false}
            multiple
            maxFileSize={10000000}
            minFileSize={1}
            onChange={handleFileUpload}>
            {isDragging => (
               <View width="100%" minHeight="500px" backgroundColor={isDragging && 'yellow'}>
                  {noteFiles.map(file => (
                     <Link key={file.id} to={`${match.url}/preview/${file.id}`}>
                        <View
                           display="inline-block"
                           height="150px[a-d] 200px[e-f]"
                           marginRight="5px"
                           marginBottom="5px"
                           backgroundColor="#ddd">
                           <img
                              onDragStart={(e) => {
                                 e.preventDefault()
                                 e.stopPropagation()
                              }}
                              src={file.thumbnailUrlW500}
                              height="100%"
                              alt={file.name}
                           />
                        </View>
                     </Link>
                  ))}
                  {uploadsInProgress.map(upload => (
                     <View
                        key={upload.id}
                        display="inline-block"
                        width="150px"
                        height="150px"
                        marginRight="5px"
                        marginBottom="5px"
                        backgroundColor="#ddd"
                     />
                  ))}
               </View>
            )}
         </FileInput>
         <Switch>
            <Route path={`${match.path}/preview/:fileId`}>
               <Modal
                  backgroundColor="#fff"
                  width="90%"
                  height="90%"
                  borderRadius="5px"
                  onCloseTrigger={() => history.push(match.url)}
                  overlayBackgroundColor="rgba(80,80,80, 0.97)"
                  open>
                  <ImagePreview
                     onCloseButtonClick={() => history.push(match.url)}
                     onDeleteButtonClick={(fileId) => {
                        handleDeleteFile(fileId)
                        history.push(match.url)
                     }}
                  />
               </Modal>
            </Route>
         </Switch>
      </>
   )
}

NoteMediaBlock.propTypes = {
   note: PropTypes.object.isRequired
}

export default NoteMediaBlock
